import { AxiosError, AxiosResponse } from 'axios'
import { camelize } from 'casing'
import {
  errorLogger,
  LoadingTypes,
  startLoading,
  stopLoading,
} from '@labrav/common'

import {
  generateEmbedUrl,
  getOverviewData,
  getWorkatoIframeUrlApi,
} from '../../api/marketplace/index'
import type { AppDispatch, RootState } from '../../store/types'
import {
  OverviewActionTypes,
  type GenerateEmbedUrlApiData,
  type GenerateEmbedUrlIframeParams,
  type GetFlyoutOverviewParams,
  type GetWorkatoIframeUrlParams,
  type OverviewState,
  type WorkatoIframeUrlApiData,
} from './types'
import { getOverviewDataToDispatch } from './utils'

export { OverviewActionTypes, OverviewActionTypes as FlyoutOverviewActionTypes }

export const getFlyoutOverview =
  ({ partnerId }: GetFlyoutOverviewParams) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FLYOUT_OVERVIEW))
    try {
      const response = await getOverviewData({ partnerId })
      const overviewData = camelize(response?.data ?? {}) as OverviewState
      await dispatch(setOverviewData(getOverviewDataToDispatch(overviewData)))
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FLYOUT_OVERVIEW))
    }
  }

export const generateEmbedUrlIframe =
  ({ partnerId, data }: GenerateEmbedUrlIframeParams) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERATE_EMBED_LOADING))

    try {
      const response = await generateEmbedUrl(data, partnerId)
      const { embed_url, signature } = response.data as GenerateEmbedUrlApiData
      return { embed_url, signature }
    } catch (error: any) {
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERATE_EMBED_LOADING))
    }
  }

export const getWorkatoIframeUrl =
  ({ workatoCustomerId, path }: GetWorkatoIframeUrlParams) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.WORKATO_LOADING))

    try {
      const response = await getWorkatoIframeUrlApi({ workatoCustomerId, path })
      const { link } = response.data as WorkatoIframeUrlApiData
      return { link }
    } catch (error: any) {
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      await dispatch(stopLoading(LoadingTypes.WORKATO_LOADING))
    }
  }

export const setOverviewData = (overviewData: OverviewState) => ({
  type: OverviewActionTypes.SET_OVERVIEW,
  payload: overviewData,
})
